/* eslint-disable @typescript-eslint/ban-ts-comment */
/* There are some TS compiler warnings/errors about circular type definitions in react-hook-form methods */
/* Disabling those warnings with @//ts-ignore tag */

import MainButton from '@ui-kit/components/mainButton';
import { Loader } from '@ui-kit/components/loader';
import { t, useSubject } from '@base/utils/helpers';
import React, { useState, ForwardRefExoticComponent, RefAttributes, useEffect, Fragment } from 'react';
import enEN from 'rc-picker/lib/locale/en_US';
import Picker from 'rc-picker/lib/Picker';
import generateConfig from 'rc-picker/lib/generate/moment';
import PhoneInput from 'react-phone-input-2';
import { container } from 'tsyringe';
import { IUserService } from '@base/features/user/model/interfaces';
import styles from '../styles/editAccountPage.module.scss';
import { History } from 'history';
import { AvatarPicker } from '@base/features/avatar/avatarPicker';
import moment from 'moment';
import ReactFlagsSelect from 'react-flags-select';
import { Controller, useForm } from 'react-hook-form';
import ChangePassword from '@base/features/user/components/changePassword';
import FormError from '@ui-kit/components/formError';
import PhoneVerification from '@base/features/auth/components/phoneVerification';
import { phoneIsValid } from '@utils/functions';
import { ReactComponent as Undo } from '@ui-kit/icons/undo.svg';
import { states } from '@base/utils/states';
import { gender } from '@base/utils/gender';
import { maritalStatus } from '@base/utils/maritalStatus';
import { objectKeys } from '@utils/functions';
import { IAuthService } from '@base/features/auth/model/interfaces';
import { useAsyncData } from '@utils/hooks';

export type PickerHandle<T> = T extends ForwardRefExoticComponent<RefAttributes<infer T2>> ? T2 : any;

export default function EditAccountPage() {
  const userService: IUserService = container.resolve('userService');
  const authService: IAuthService = container.resolve('authService');
  const { data: personalInfo } = useAsyncData(() => userService.getUserInfo());
  const userSession = useSubject(authService.userSession);
  const [changePhone, setChangePhone] = useState(false);
  const history: History = container.resolve('history');
  let childHandle: PickerHandle<typeof AvatarPicker>;

  const defaultValues = {
    about: personalInfo?.about,
    login: personalInfo?.login_name,
    name: {
      lastName: personalInfo?.real_name.last_name,
      firstName: personalInfo?.real_name.first_name,
    },
    yob: moment(personalInfo?.yob?.toString()),
    email: personalInfo?.email,
    phone: personalInfo?.phone,
    address: {
      country: personalInfo?.address.country_code,
      city: personalInfo?.address.city,
      state: personalInfo?.address.state,
      street: personalInfo?.address.street,
      zip: personalInfo?.address.zip,
    },
    gender: personalInfo?.gender,
    status: personalInfo?.marital_status,
  };

  const {
    register,
    formState: { errors, dirtyFields, isSubmitting },
    handleSubmit,
    watch,
    setError,
    getValues,
    setValue,
    control,
    reset,
  } = useForm({ defaultValues });

  useEffect(() => reset(defaultValues), [personalInfo]);

  function submitActions(data: { [x: string]: any }, actions: keyof typeof dirtyFields) {
    return {
      about: () => userService.changeAbout(data.about),
      address: () => userService.changeAddress(data.address),
      login: () => userService.changeLogin(data.login),
      name: () => userService.changeName(data.name),
      email: () => userService.changeEmail(data.email),
      gender: () => userService.changeGender(data.gender),
      status: () => userService.changeMaritalStatus(data.status),
      phone: () => '',
      yob: () => userService.changeYearOfBirth(data.yob),
    }[actions];
  }

  const onSubmit = handleSubmit(async (data) => {
    const fields = objectKeys(dirtyFields);

    for (const field of fields) {
      try {
        await submitActions(data, field)();
      } catch (error: any) {
        //@ts-ignore
        setError(field, {
          type: 'manual',
          message: error.message,
        });
      }
    }
    history.goBack();
  });

  if (!personalInfo || !userSession) return <Loader />;

  return (
    <div className={styles.editAccountPage}>
      <h1>{t('edit_account')}</h1>
      <main>
        <div className={styles.content}>
          <section>
            <div className={styles.avatar}>
              <AvatarPicker
                userId={personalInfo.user_id}
                usr={userSession.usr}
                session={userSession.session}
                ref={(c) => (childHandle = c)}
              />
              <div className={styles.changeButtons}>
                <MainButton onClick={() => childHandle.handlePickerAvatar()}>{t('select_icon')}</MainButton>
                <ChangePassword />
              </div>
            </div>
            <label htmlFor="about" style={{ textAlign: 'center' }}>
              {t('about_me')}
            </label>
            {/* @ts-ignore */}
            <textarea {...register('about')} />
          </section>
          <section>
            <form>
              <label htmlFor={'login'}>{t('login')}</label>
              <div>
                <input {...register('login')} />
                <FormError errors={errors} field={'login'} />
              </div>
              <label htmlFor={'name.firstName'}>{t('first_name')}</label>
              <input {...register('name.firstName')} />
              <label htmlFor={'name.lastName'}>{t('last_name')}</label>
              <div>
                <input id={'name.lastName'} {...register('name.lastName')} />
                <FormError errors={errors} field={'name'} />
              </div>
              <label htmlFor={'yob'}>{t('year_of_birth')}</label>
              <Controller
                name={'yob'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Picker
                    generateConfig={generateConfig}
                    id={'yob'}
                    picker="year"
                    locale={enEN}
                    onChange={onChange}
                    value={value as moment.Moment}
                  />
                )}
              />
              <label htmlFor="gender">{t('gender')}</label>
              <div className={styles.gender}>
                {gender.list().map((g) => (
                  <Fragment key={g.code}>
                    <input
                      id={g.code}
                      type="radio"
                      defaultChecked={g.code === personalInfo.gender}
                      value={g.code}
                      {...register('gender')}
                    />
                    <label htmlFor={g.code}>{t(g.name)}</label>
                  </Fragment>
                ))}
              </div>
              <label htmlFor="status">{t('status')}</label>
              <select {...register('status')}>
                {maritalStatus.list().map((status) => (
                  <option value={status.code} key={status.code}>
                    {status.name}
                  </option>
                ))}
              </select>
              <label htmlFor="email">{t('email')}</label>
              <input {...register('email')} />
              <label htmlFor="phone">{t('phone')}</label>
              {changePhone ? (
                <div className={styles.phoneInput}>
                  <Controller
                    name="phone"
                    control={control}
                    defaultValue={personalInfo.phone}
                    render={({ field: { value } }) => (
                      <PhoneInput
                        country="us"
                        containerStyle={{ marginBottom: '20px' }}
                        onChange={(phone) => setValue('phone', phone)}
                        value={value}
                        isValid={phoneIsValid}
                      />
                    )}
                  />
                  <div style={{ display: 'flex' }}>
                    <PhoneVerification
                      title="Send code"
                      phone={watch('phone')}
                      onVerified={async (token) => {
                        try {
                          const phone = getValues('phone');
                          if (!phone) return;
                          await userService.changePhone(phone, token);
                          setChangePhone(false);
                        } catch (e: any) {
                          setError('phone', {
                            type: 'manual',
                            message: e.message,
                          });
                        }
                      }}
                      onError={(error) =>
                        setError('phone', {
                          type: 'manual',
                          message: error.message,
                        })
                      }
                    />
                    <MainButton onClick={() => setChangePhone(false)} style={{ marginLeft: '1rem' }}>
                      <Undo />
                    </MainButton>
                  </div>
                  <FormError errors={errors} field="phone" />
                </div>
              ) : (
                <div className={styles.phone}>
                  <span>{personalInfo.phone}</span>
                  <MainButton onClick={() => setChangePhone(true)}>{t('edit')}</MainButton>
                </div>
              )}
              <label htmlFor="country">{t('country')}</label>
              <Controller
                name="address.country"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ReactFlagsSelect
                    className="country-select"
                    selected={value || ''}
                    onSelect={onChange}
                    searchable
                  />
                )}
              />
              {(watch('address.country') || personalInfo.address.country_code) === 'US' && (
                <label htmlFor="address.state">{t('state')}</label>
              )}
              {(watch('address.country') || personalInfo.address.country_code) === 'US' && (
                <select {...register('address.state', { shouldUnregister: true })}>
                  {states.list().map((state) => (
                    <option value={state.code} key={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              )}
              <label htmlFor="address.city">{t('city')}</label>
              <input {...register('address.city')} />
              <label htmlFor="address.street">{t('address')}</label>
              <input {...register('address.street')} />
              <label htmlFor="address.zip">{t('zip')}</label>
              <div>
                <input id="zip" {...register('address.zip')} />
                <FormError errors={errors} field="address" />
              </div>
            </form>
          </section>
        </div>
        <div className={styles.buttons}>
          <MainButton onClick={() => history.goBack()}>{t('cancel')}</MainButton>
          <MainButton onClick={onSubmit} loading={isSubmitting}>
            {t('save')}
          </MainButton>
        </div>
      </main>
    </div>
  );
}
